// React
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import {
  BrowserRouter as Router,
  Link,
  useLocation,
  useHistory,
} from "react-router-dom";

// Material
import {
  makeStyles,
  useTheme,
  createMuiTheme,
  MuiThemeProvider,
} from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Avatar from "@material-ui/core/Avatar";
import { Tooltip, Typography } from "@material-ui/core";

import SvgIcon from "@material-ui/core/SvgIcon";

import PowerIcon from "@material-ui/icons/Power";
import PublicIcon from "@material-ui/icons/Public";

import clsx from "clsx";

import Popover from "@material-ui/core/Popover";
import MenuItem from "@material-ui/core/MenuItem";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import Divider from "@material-ui/core/Divider";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";

import DocsIcon from "@material-ui/icons/Description";
import LearnIcon from "@material-ui/icons/MenuBook";
import InfoIcon from "@material-ui/icons/InfoOutlined";

import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";

// Massless
import { useAuth } from "../context/AuthContext";
import { useCommands } from "../context/CommandBarContext";

import { sAnalyticsTrack, trackUser } from "../massless/AnalyticsClient";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {},
  masslessSpiral: { marginTop: "30px" },
  preLoginNav: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flexGrow: 1,
      flexDiection: "row-reverse",
      width: "100%",
    },
  },
  postLoginNav: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flex: "auto",
    },
  },
  userAvatar: {
    width: "40px",
    height: "40px",
    marginBottom: "20px",
    cursor: "pointer",
    alignSelf: "center",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    border: "none",
    overflowX: "hidden",
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up("sm")]: {
      width: "60px",
    },
  },
  loginNavButtons: {
    display: "flex",
    flexDirection: "column",
    margin: "auto",
  },
  popover: { boxShadow: "0px 2px 6px rgba(33, 6, 170, 0.3)" },
  menulink: { justifyContent: "center" },
  dialogTitle: { padding: "24px 24px 0" },
  dialogButtons: {
    display: "flex",
    justifyContent: "center",
    padding: "24px 0",
  },
  buttonRoot: {
    boxShadow: "inset 0px 0px 0px 3px #6E54F4",
    textTransform: "none",
    fontSize: 16,
    fontWeight: 400,
    padding: "6px 12px",
    lineHeight: 1.5,
    backgroundColor: "rgba(1,1,1,0)",
    color: "#6E54F4",
    borderRadius: "50px",
    "&:hover": {
      color: "#ffffff",
      background: "#6E54F4",
      boxShadow:
        "0px 4px 4px rgba(37, 14, 156, 0.2), 0px 2px 2px rgba(37, 14, 156, 0.2)",
      // border: "3px solid #F0F2F5",
      // boxShadow: "inset 0px 0px 0px 3px #F0F2F5",
      // boxShadow: "none",
    },
    "&:active": {
      boxShadow: "none",
      backgroundColor: "#6E54F4",
      borderColor: "#6E54F4",
    },
    // "&:focus": {
    //   boxShadow: "0 0 0 0.2rem rgba(110,84,244,.5)",
    // },
  },
  pluginWrapper: { display: "flex", flexDirection: "column", margin: "0 20px" },
  docsButton: {
    marginTop: "5px",
    textTransform: "capitalize",
    fontWeight: 400,
  },
}));

//#region SVG Icons
function BlenderIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M12.4683 13.1208C12.5093 12.3817 12.8687 11.7305 13.4108 11.269C13.9425 10.8157 14.658 10.5385 15.4388 10.5385C16.2188 10.5385 16.9343 10.8157 17.4664 11.269C18.0081 11.7305 18.3672 12.3817 18.409 13.1201C18.45 13.8795 18.1472 14.5851 17.6155 15.1079C17.0734 15.6397 16.3023 15.9736 15.4392 15.9736C14.5756 15.9736 13.8031 15.6397 13.2617 15.1079C12.7293 14.5851 12.4269 13.8795 12.4683 13.1208ZM7.73823 14.6144C7.74345 14.9036 7.8348 15.4658 7.97239 15.9045C8.26098 16.8336 8.75091 17.6928 9.43211 18.4503C10.1312 19.2289 10.9925 19.8542 11.9869 20.2981C13.0324 20.7645 14.1648 21.0019 15.3415 21C16.516 20.9985 17.6487 20.7563 18.6942 20.2864C19.6886 19.838 20.5492 19.2101 21.2471 18.4312C21.928 17.6707 22.4172 16.8095 22.7065 15.8804C22.8519 15.411 22.944 14.9348 22.9809 14.4567C23.0171 13.9857 23.0018 13.514 22.9354 13.0427C22.8057 12.1241 22.4902 11.2626 22.0044 10.4769C21.56 9.75511 20.9873 9.12305 20.3064 8.59127L20.3079 8.59014L13.4355 3.27491C13.4291 3.2704 13.4243 3.26514 13.4176 3.26101C12.9664 2.91212 12.2084 2.91325 11.7125 3.26289C11.211 3.61629 11.1536 4.20065 11.5999 4.56907L11.598 4.57095L14.4645 6.91854L5.72817 6.92793C5.72407 6.92793 5.7196 6.92793 5.71661 6.92793C4.99439 6.92868 4.30014 7.40602 4.16293 8.00916C4.02161 8.62357 4.51229 9.13319 5.26285 9.13582L5.26173 9.13845L9.68976 9.12981L1.78784 15.2393C1.77777 15.2469 1.76696 15.2547 1.75764 15.2619C1.0123 15.8369 0.771065 16.7926 1.24086 17.3977C1.71737 18.0128 2.73042 18.0139 3.48358 17.401L7.79603 13.846C7.79603 13.8464 7.73339 14.3264 7.73823 14.6144ZM18.8199 16.2214C17.9313 17.1333 16.6871 17.6504 15.3411 17.653C13.9932 17.6553 12.749 17.143 11.8605 16.2327C11.4261 15.7892 11.1073 15.2788 10.9105 14.735C10.7177 14.2006 10.6428 13.6335 10.6924 13.0615C10.7393 12.5023 10.9045 11.9686 11.1685 11.486C11.4276 11.0117 11.7848 10.5828 12.2255 10.2201C13.0894 9.511 14.189 9.12718 15.3393 9.12568C16.4906 9.12418 17.5894 9.50424 18.4541 10.211C18.8941 10.5723 19.2509 10.9993 19.51 11.4729C19.7751 11.9551 19.9392 12.4869 19.988 13.0476C20.0369 13.6192 19.9619 14.1852 19.7691 14.72C19.5719 15.2653 19.2542 15.776 18.8199 16.2214Z" />
    </SvgIcon>
  );
}

function UnityIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M18.2132 17.6438L14.7144 11.7527L18.2132 5.85977L19.9033 11.7527L18.2132 17.6438ZM10.4146 17.0781L6.01423 12.7082H13.0099L16.5069 18.6011L10.4146 17.0781ZM10.4146 6.42374L16.5087 4.90066L13.0099 10.7936H6.01423L10.4146 6.42374ZM19.3681 2L11.4025 4.07626L10.2242 6.09865L7.83186 6.08069L2 11.7527L7.83006 17.4229L10.2206 17.4049L11.4025 19.4273L19.3663 21.5036L21.5 13.7607L20.2876 11.7545L21.5 9.74827L19.3681 2Z" />
    </SvgIcon>
  );
}

function MasslessSpiral(props) {
  return (
    <SvgIcon {...props}>
      <path d="M7.62602 7.47291C7.41713 8.18112 6.92063 10.5721 6.68013 12.3173C6.43529 14.0959 5.8266 16.7106 5.19745 18.0244C4.30858 19.8821 3.15256 20.4 2.04303 20.4C0.438854 20.4 0 19.3896 0 18.9625C0 18.3841 0.763036 17.6629 1.70025 13.4587C2.63746 9.25456 2.8321 6.67323 3.76621 5.33405C4.25899 4.62769 5.2272 3.60245 6.90824 3.60245C7.7376 3.60245 8.5589 3.8929 9.23516 4.4528C9.52091 4.68887 9.52711 4.86129 9.23516 4.86129C8.33327 4.86129 7.85165 6.70536 7.62602 7.47291ZM19.436 4.85882C19.7497 4.85882 19.6722 4.64129 19.423 4.43611C19.2247 4.27358 18.43 3.59998 17.0279 3.59998C15.9085 3.59998 14.6452 4.23156 13.9249 5.41006C12.8334 7.19728 12.3809 11.0214 11.9997 13.112C11.3681 16.5759 10.6937 19.1436 9.68951 19.1436C9.37772 19.1436 9.39942 19.3599 9.68951 19.5923C10.3447 20.1176 11.179 20.3975 11.9997 20.3975C13.2419 20.3975 14.5157 19.7579 15.1535 18.5948C16.1626 16.7545 16.6498 13.4037 17.0998 10.891C17.2647 9.97205 17.5914 8.23303 17.8257 7.46982C18.06 6.7066 18.5342 4.85882 19.436 4.85882ZM9.37834 18.2444C10.6069 18.2444 11.2974 14.8115 11.7077 12.3686C11.7573 12.072 11.7679 11.7685 11.7096 11.4731C11.502 10.4269 10.9986 5.75737 9.52029 5.75737C8.50993 5.75737 7.92355 7.67375 7.20081 11.6202C7.14254 11.9391 7.12952 12.2679 7.19833 12.5849C7.50205 13.9729 7.93967 18.2444 9.37834 18.2444ZM21.957 20.3975C23.5605 20.3975 24 19.3871 24 18.9601C24 18.3816 22.932 17.0999 21.9526 11.7871C21.6154 9.95722 21.1797 5.75737 19.7199 5.75737C18.3947 5.75737 17.7005 9.93806 17.3837 11.721C17.3267 12.0405 17.3285 12.3674 17.3862 12.6869C17.7686 14.8115 18.3153 17.0029 18.8038 18.0226C19.692 19.8802 20.8474 20.3975 21.957 20.3975Z" />
    </SvgIcon>
  );
}

function HomeIcon(props) {
  return (
    <SvgIcon {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.88867 10L11.89 3.99867L17.8913 10H17.89V20H5.89001V10H5.88867ZM3.89001 11.9987L2.4132 13.4755L1 12.0623L10.477 2.58529C11.2574 1.8049 12.5226 1.8049 13.303 2.58529L22.78 12.0623L21.3668 13.4755L19.89 11.9987V20C19.89 21.1046 18.9946 22 17.89 22H5.89001C4.78545 22 3.89001 21.1046 3.89001 20V11.9987Z"
      />
    </SvgIcon>
  );
}

function DocumentationIcon(props) {
  return (
    <SvgIcon {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21 22H6C4.34315 22 3 20.6569 3 19V5C3 3.34315 4.34315 2 6 2H21V18C20.4477 18 20 18.4477 20 19C20 19.5523 20.4477 20 21 20V22ZM18 19C18 18.6494 18.0602 18.3128 18.1707 18H6C5.44772 18 5 18.4477 5 19C5 19.5523 5.44772 20 6 20H18.1707C18.0602 19.6872 18 19.3506 18 19ZM6 4H19V16H6C5.64936 16 5.31278 16.0602 5 16.1707V5C5 4.44772 5.44772 4 6 4Z"
      />
    </SvgIcon>
  );
}
//#endregion

const emails = ["username@gmail.com", "user02@gmail.com"];

function PluginDialog(props) {
  const classes = useStyles();
  const { onClose, selectedValue, open } = props;

  const handleClose = () => {
    onClose(selectedValue);
  };

  const handleListItemClick = (value) => {
    onClose(value);
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        padding="16px 24px 0"
      >
        <Typography variant="h5">Get our Plugins</Typography>
      </DialogTitle>
      <DialogContent style={{ padding: "8px" }}>
        <DialogContentText style={{ padding: "0 16px" }}>
          In order to start adding to your Space, you need to connect to it with
          one of our plugins.
        </DialogContentText>
        <DialogContentText style={{ padding: "0 16px" }}>
          Click below to download the plugins or check out the documentation to
          learn how to get started.
        </DialogContentText>
        <Box className={classes.dialogButtons}>
          <Box className={classes.pluginWrapper}>
            <Button
              startIcon={<BlenderIcon />}
              size="large"
              className={classes.buttonRoot}
              classes={{ label: classes.buttonLabel }}
              variant="contained"
              href={process.env.REACT_APP_BLENDER_PLUGIN}
              onClick={() => {
                trackUser("side-nav-dialog-blender-plugin-click");
              }}
            >
              Get Blender Plugin
            </Button>
            <Button
              variant="text"
              className={classes.docsButton}
              color="primary"
              onClick={() => {
                window.open(
                  "https://docs.space.massless.io/getting-started#blender"
                );
                trackUser("side-nav-dialog-blender-docs-click");
              }}
            >
              Blender Documentation
            </Button>
          </Box>
          <Box className={classes.pluginWrapper}>
            <Button
              href={process.env.REACT_APP_UNITY_PLUGIN}
              startIcon={<UnityIcon />}
              className={classes.buttonRoot}
              classes={{ label: classes.buttonLabel }}
              variant="contained"
              onClick={() => {
                trackUser("side-nav-dialog-unity-plugin-click");
              }}
            >
              Get Unity Plugin
            </Button>
            <Button
              variant="text"
              className={classes.docsButton}
              color="primary"
              onClick={() => {
                window.open(
                  "https://docs.space.massless.io/getting-started#unity"
                );
                trackUser("side-nav-dialog-unity-docs-click");
              }}
            >
              Unity Documentation
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

PluginDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedValue: PropTypes.string.isRequired,
};

export default function SideNav(props) {
  //#region  Analytics
  let { profile, authSignOut, isAuthenticated } = useAuth();
  //#endregion

  //#region Nav
  const classes = useStyles();
  let history = useHistory();
  //let { open, setOpen } = useSnackbar();

  let [nickname, setNickname] = React.useState("");
  let [email, setEmail] = React.useState("");
  let [picture, setPicture] = React.useState("");

  React.useEffect(() => {
    if (profile == null) return;
    setNickname(profile.nickname);
    setPicture(profile.picture);
    setEmail(profile.email);
  }, [profile]);

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setState({ ...state, [anchor]: open });
  };

  const preLoginMenu = (anchor) => (
    <div
      className={clsx(classes.list, {
        [classes.fullList]: anchor === "top" || anchor === "bottom",
      })}
      role="presentation"
      onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}
    >
      <List>
        {[
          {
            label: "Learn More",
            link: "https://massless.io/massless-space/",
            id: "nav-learn-more-click",
            icon: <LearnIcon />,
          },
          {
            label: "Docs",
            link: "https://docs.space.massless.io/",
            id: "nav-learn-more-click",
            icon: <DocsIcon />,
          },
        ].map((buttonInfo) => (
          <ListItem
            button
            key={buttonInfo.label}
            component="a"
            href={buttonInfo.link}
            onClick={() => {
              trackUser(buttonInfo.id);
            }}
          >
            <ListItemIcon color="white">{buttonInfo.icon}</ListItemIcon>
            <ListItemText primary={buttonInfo.label} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  const postLoginMenu = (anchor) => (
    <div
      className={clsx(classes.list, {
        [classes.fullList]: anchor === "top" || anchor === "bottom",
      })}
      role="presentation"
      onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}
    >
      <List>
        {/* {['Projects', 'Plugins', 'Docs'].map((text, index) => ( */}
        <ListItem button key="Account" component={Link} to="/account">
          <ListItemIcon color="white">
            <Avatar
              alt="User Avatar"
              className={classes.userAvatar}
              src={picture}
            />
          </ListItemIcon>
          <ListItemText primary={nickname} />
        </ListItem>
        <Divider />
        <ListItem button key="Projects" component={Link} to="/">
          <ListItemIcon color="white">
            <PublicIcon />
          </ListItemIcon>
          <ListItemText primary="Global Studio" />
        </ListItem>
        <ListItem button key="Plugins" component={Link} to="/plugins">
          <ListItemIcon color="white">
            <PowerIcon />
          </ListItemIcon>
          <ListItemText primary="Plugins" />
        </ListItem>
        <ListItem
          button
          key="Docs"
          component="a"
          href="https://docs.space.massless.io/"
          target="blank"
        >
          <ListItemIcon color="white">
            <DocsIcon />
          </ListItemIcon>
          <ListItemText primary="Docs" />
        </ListItem>
        {/* ))} */}
      </List>
    </div>
  );

  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  //#endregion

  //#region Sidebar State
  const [open, setOpen] = React.useState(false);
  //#endregion

  //#region Profile Popover
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const profileOpen = Boolean(anchorEl);
  const id = profileOpen ? "profile-popover" : undefined;
  //#endregion

  //#region Plugin Dialog
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState(emails[1]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = (value) => {
    setDialogOpen(false);
    setSelectedValue(value);
  };
  //#endregion

  //#region Commands
  const { registerCommands } = useCommands();

  const addCommands = () => {
    registerCommands([
      {
        id: "explore",
        name: "Explore",
        description: "Explore Spaces",
        shortcut: "",
        command: () => {
          history.replace("/");
        },
      },
      {
        id: "connect-to-unity",
        name: "Connect to Unity",
        description: "Download Unity Plugin",
        shortcut: "",
        command: () => {
          window.open(process.env.REACT_APP_UNITY_PLUGIN);
          window.open("https://docs.space.massless.io/getting-started#unity");
        },
      },
      {
        id: "connect-to-blender",
        name: "Connect to Blender",
        description: "Download Blender Addon",
        shortcut: "",
        command: () => {
          window.open(process.env.REACT_APP_BLENDER_PLUGIN);
          window.open("https://docs.space.massless.io/getting-started#blender");
        },
      },
      {
        id: "documentation",
        name: "Documentation",
        description: "Open documentation",
        shortcut: "",
        command: () => {
          window.open("https://docs.space.massless.io/");
        },
      },
      {
        id: "plugins",
        name: "Plugins",
        description: "Open plugins page",
        shortcut: "",
        command: () => {
          history.replace("/plugins");
        },
      },
      {
        id: "account",
        name: "Account",
        description: "Open account page",
        shortcut: "",
        command: () => {
          history.replace("/account");
        },
      },
    ]);
  };
  //#endregion

  //#region On Mount
  React.useEffect(() => {
    addCommands();
  }, []);
  //#endregion

  return (
    <>
      <div className={classes.root}>
        <CssBaseline />
        <Drawer
          variant="permanent"
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            }),
          }}
        >
          <Tooltip
            title={"v" + process.env.REACT_APP_VERSION}
            placement="right"
          >
            <Link
              to="/"
              className={classes.masslessSpiral}
              onClick={() => {
                trackUser("side-bar-logo-click");
              }}
            >
              <IconButton>
                <MasslessSpiral fontSize="large" />
              </IconButton>
            </Link>
          </Tooltip>
          {isAuthenticated ? (
            <>
              <Box className={classes.loginNavButtons}>
                <Tooltip title={"Explore"} placement="right">
                  <IconButton
                    href="/"
                    onClick={() => {
                      trackUser("side-bar-explore-click");
                    }}
                  >
                    <HomeIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={"Get Blender Plugin"} placement="right">
                  <IconButton
                    onClick={() => {
                      handleDialogOpen();
                      trackUser("side-nav-unity-click");
                    }}
                  >
                    <BlenderIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={"Get Unity Plugin"} placement="right">
                  <IconButton
                    onClick={() => {
                      handleDialogOpen();
                      trackUser("side-nav-unity-click");
                    }}
                  >
                    <UnityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={"Documentation"} placement="right">
                  <IconButton
                    onClick={() => {
                      window.open("https://docs.space.massless.io/");
                      trackUser("side-bar-documentation-click");
                    }}
                  >
                    <DocumentationIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <PluginDialog
                selectedValue={selectedValue}
                open={dialogOpen}
                onClose={handleDialogClose}
              />
              <IconButton
                className={classes.userAvatar}
                onClick={(e) => {
                  handleClick(e);
                  trackUser("nav-avatar-click");
                }}
              >
                <Avatar
                  aria-describedby={id}
                  alt="User Avatar"
                  src={picture}
                  // component={Link}
                  // to={"/account"}
                />
              </IconButton>
              <Popover
                classes={{ root: classes.popover }}
                id={id}
                open={profileOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <MenuItem
                  className={classes.menulink}
                  component={Link}
                  to={"/account/spaces"}
                >
                  <Box display="flex">
                    <Avatar
                      style={{
                        width: "50px",
                        height: "50px",
                        margin: "10px 10px 10px 0",
                      }}
                      aria-describedby={id}
                      alt="User Avatar"
                      src={picture}
                    />
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignSelf="center"
                    >
                      <Typography style={{ textTransform: "capitalize" }}>
                        {nickname}
                      </Typography>
                      <Typography
                        style={{ fontSize: "0.75rem", color: "#7D8BAE" }}
                      >
                        {email}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem
                  className={classes.menulink}
                  component={Link}
                  to={"/account/spaces"}
                  onClick={handleClose}
                >
                  Your Spaces
                </MenuItem>
                <Divider />
                <MenuItem
                  className={classes.menulink}
                  component={Link}
                  to={"/account/upgrade"}
                  onClick={handleClose}
                >
                  Upgrade Account
                </MenuItem>
                <Divider />
                <MenuItem
                  className={classes.menulink}
                  onClick={() => {
                    authSignOut();
                    handleClose();
                  }}
                >
                  Logout
                </MenuItem>
              </Popover>
            </>
          ) : (
            <Box className={classes.loginNavButtons}>
              <Tooltip title={"Learn More"} placement="right">
                <IconButton
                  onClick={() => {
                    window.open("https://massless.io/massless-space");
                    trackUser("prelogin-side-bar-learnmore-click");
                  }}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={"Documentation"} placement="right">
                <IconButton
                  onClick={() => {
                    window.open("https://docs.space.massless.io/");
                    trackUser("prelogin-side-bar-doucmentation-click");
                  }}
                >
                  <DocumentationIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Drawer>
      </div>
    </>
  );
}
